import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import Popover from '@material-ui/core/Popover';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import { Context } from '../../context';
import { useElectionAddendum } from './ElectionAddendumContext';
import { buildElectionAddendumOwedLaterMessage } from './electionAddendumDueMessage';
import { VisitElectionAddendumStatus } from './electionAddendumPromptRules';
import defaults from '../../translations/en.json';

/** EA chip in the visit header; VisitPromptStack anchors to it without HOPE. */
export const EA_VISIT_ICON_ID = 'election-addendum-visit-icon';

const IconDiv = styled.div<{ color?: string; $disabled?: boolean }>`
  ${(props: any) => `background-color: ${props.color || '#880808'};`}
  ${(props: any) =>
    `color: ${props.theme.palette.common.white};`}
  width: 2.5em;
  height: 20px;
  cursor: ${(props: any) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props: any) => (props.$disabled ? 0.6 : 1)};
  padding: 1px;
  border-radius: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-family: 'Muli', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1;
  margin-right: 4px;
`;

const useStyles = makeStyles((theme) => ({
  completedText: {
    padding: theme.spacing(1.5, 2),
  },
}));

const isActivateKey = (e: React.KeyboardEvent) =>
  e.key === 'Enter' || e.key === ' ';

/** Tooltip when the chip is greyed. */
const getBlockedTitle = (
  status: VisitElectionAddendumStatus,
  requiredDateShifted: number | undefined,
): string | undefined => {
  if (status === 'completedOwedLater') {
    return typeof requiredDateShifted === 'number'
      ? buildElectionAddendumOwedLaterMessage(requiredDateShifted)
      : defaults.electionAddendum;
  }
  if (status === 'beforeTrigger') {
    return defaults.electionAddendumBeforeTrigger;
  }
  return status === 'error' ? defaults.electionAddendumStatusError : undefined;
};

/**
 * Visit-header EA chip, from the visit's EA status: hidden while loading or
 * when nothing is owed, green when the visit has EAC (click shows a
 * "completed" popup), greyed with the reason when EA can't be filled from
 * this visit, clickable otherwise (opens the prompt).
 */
export const ElectionAddendumIcon = () => {
  const classes = useStyles();
  const { palette } = useTheme();
  const { patient } = useContext(Context);
  const { patientGlobal } = useContext(GlobalContext);
  const { status, openFromIcon } = useElectionAddendum();
  const [completedAnchor, setCompletedAnchor] = useState<HTMLElement | null>(
    null,
  );

  if (status === 'hidden' || status === 'loading') {
    return null;
  }

  if (status === 'completed') {
    const handleCompletedOpen = (e: React.SyntheticEvent<HTMLElement>) =>
      setCompletedAnchor(e.currentTarget);
    const handleCompletedClose = () => setCompletedAnchor(null);

    return (
      <>
        <IconDiv
          id={EA_VISIT_ICON_ID}
          color={palette.success.main}
          title={defaults.electionAddendum}
          onClick={handleCompletedOpen}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (isActivateKey(e)) {
              e.preventDefault();
              handleCompletedOpen(e);
            }
          }}
        >
          EA
        </IconDiv>
        <Popover
          open={!!completedAnchor}
          anchorEl={completedAnchor}
          onClose={handleCompletedClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {/* Plain div: inherits the page font like the HOPE prompt text. */}
          <div className={classes.completedText}>
            {defaults.electionAddendumCompleted}
          </div>
        </Popover>
      </>
    );
  }

  if (status !== 'allowed') {
    const requiredDateShifted = (patientGlobal ?? patient)?.eaDetails
      ?.requiredDateShifted;
    return (
      <IconDiv
        id={EA_VISIT_ICON_ID}
        color={palette.warning.main}
        title={getBlockedTitle(status, requiredDateShifted)}
        aria-disabled="true"
        $disabled
      >
        EA
      </IconDiv>
    );
  }

  return (
    <IconDiv
      id={EA_VISIT_ICON_ID}
      color={palette.warning.main}
      title={defaults.electionAddendum}
      onClick={openFromIcon}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (isActivateKey(e)) {
          e.preventDefault();
          openFromIcon();
        }
      }}
    >
      EA
    </IconDiv>
  );
};

export default ElectionAddendumIcon;
